import type { CityCount, EventOverview } from '@mmef/shared/api';

/*
 * The homepage's words, in one place.
 *
 * <p><b>This has a twin.</b> HomePageHtml.java writes the same page on the server, for everything
 * that reads it before the script runs. Every string here is also built there, by the same rules,
 * and the tests on both sides assert the same wording — because the moment React takes over, a
 * visitor is looking straight at any difference.
 */

export const HOME_TITLE = 'Muslim Marriage Events across the UK';
export const HOME_HEADING = 'Muslim marriage events across the UK';

/** Named in the description, busiest first. More than three stops reading as a sentence. */
const CITIES_IN_DESCRIPTION = 3;

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export function homeDescription(overview: EventOverview): string {
  if (overview.totalEvents === 0) return 'Find upcoming Muslim marriage events across the UK.';
  const names = overview.cities.slice(0, CITIES_IN_DESCRIPTION).map((c) => c.name);
  const where = names.length === 0 ? 'across the UK' : `in ${names.join(', ')} and more`;
  return (
    `${overview.totalEvents} upcoming Muslim marriage ${plural(overview.totalEvents, 'event', 'events')} ${where}, ` +
    `from ${overview.organiserCount} ${plural(overview.organiserCount, 'organiser', 'organisers')}.`
  );
}

/** "31 upcoming events from 9 organisers" */
export function homeSummary(overview: EventOverview): string {
  return (
    `${overview.totalEvents} upcoming ${plural(overview.totalEvents, 'event', 'events')} ` +
    `from ${overview.organiserCount} ${plural(overview.organiserCount, 'organiser', 'organisers')}`
  );
}

export function otherCitiesLabel(others: CityCount[]): string {
  return others.map((c) => c.name).join(', ');
}

/**
 * The keywords worth a tag on a one-line summary, in priority order, with how each is written.
 *
 * <p>Only the ones that decide whether someone can go at all, or who it's for. "Halal food" is true
 * of nearly everything listed and would crowd out the tag that says you need a passport.
 */
const TAG_LABELS: ReadonlyArray<[string, string]> = [
  ['british citizens only', 'British citizens only'],
  ['photo id required', 'Photo ID required'],
  ['invite only', 'Invite only'],
  ['at a mosque', 'At a mosque'],
  ['over 50s', 'Over 50s'],
  ['over 40s', 'Over 40s'],
  ['over 30s', 'Over 30s'],
  ['over 25s', 'Over 25s'],
];

export const MAX_TAGS = 2;

export function upcomingTags(keywords: string[]): string[] {
  const present = new Set(keywords.map((k) => k.trim().toLowerCase()));
  return TAG_LABELS.filter(([key]) => present.has(key))
    .slice(0, MAX_TAGS)
    .map(([, label]) => label);
}

/** Fixed rather than locale-formatted: browsers and Java disagree on "Sep" versus "Sept". */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Month and day as they fall in the event's own timezone — not the reader's, and not UTC. */
export function calendarDay(iso: string, timeZone: string): { month: string; day: number } {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', { timeZone, month: 'numeric', day: 'numeric' }).formatToParts(new Date(iso));
  } catch {
    // An unrecognised zone must not take the row down over a date label.
    parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'numeric', day: 'numeric' }).formatToParts(
      new Date(iso),
    );
  }
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  const day = Number(parts.find((p) => p.type === 'day')?.value);
  return { month: MONTHS[month - 1], day };
}
